// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Icon, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useResponsive, useDrawer, ResponsiveType} from '@supermove/hooks';
import {Project, ProjectModel, Stage, UserModel} from '@supermove/models';
import {Environment} from '@supermove/sdk';
import {pluralize, List, Datetime, Currency} from '@supermove/utils';

// App
import Badge from '@shared/design/components/Badge';
import Line from '@shared/design/components/Line';
import Table, {ColumnDefinitionType} from '@shared/design/components/Table';
import Text from '@shared/design/components/Text';
import OutgoingCallDrawer from '@shared/modules/Call/components/OutgoingCallDrawer';
import CommunicationStatusAction from '@shared/modules/Communication/components/CommunicationStatusAction';
import ProjectCommentsButton from '@shared/modules/Project/components/ProjectCommentsButton';
import ProjectContactActions from '@shared/modules/Project/components/ProjectContactActions';
import ProjectTagsButton from '@shared/modules/Project/components/ProjectTagsButton';
import ProjectWidgetKind from '@shared/modules/Project/enums/ProjectWidgetKind';
import {SalesLeadsFiltersType} from '@shared/modules/Sales/components/SalesLeads';

const GUTTER = 24;

const LinkButton = Styled.ButtonV2`
  flex: 1;
`;

const getProjectTagsDefinition = (): ColumnDefinitionType<ProjectModel> => {
  return {
    headerLabel: 'Tags',
    cellComponent: (project) => {
      return <ProjectTagsButton project={project} />;
    },
  };
};

const ContactActions = ({
  project,
  urlFilters,
  viewer,
}: {
  project: ProjectModel;
  urlFilters: SalesLeadsFiltersType;
  viewer: UserModel;
}) => {
  const outgoingCallDrawer = useDrawer({name: 'Outgoing Call Drawer'});
  const {primaryContact} = project.client;

  return (
    <React.Fragment>
      <ProjectContactActions
        project={project}
        handleCall={outgoingCallDrawer.handleToggle}
        handleSms={() =>
          urlFilters.handleUpdate({
            projectUuid: project.uuid,
            widget: ProjectWidgetKind.CONVERSATIONS,
          })
        }
        handleEmail={(emailTemplateUuid: string = '') => {
          const queryParams = emailTemplateUuid ? `?emailTemplateUuid=${emailTemplateUuid}` : '';
          window.open(
            `${Environment.getAppUrl()}/projects/${project.uuid}/email/compose${queryParams}`,
            '_blank',
          );
        }}
        isOfficeAppEnabled={viewer.viewingOrganization.isOfficeAppEnabled}
      />
      <OutgoingCallDrawer
        key={primaryContact.phoneNumber}
        drawer={outgoingCallDrawer}
        viewer={viewer}
        numberToCall={primaryContact.phoneNumber}
        leadName={primaryContact.fullName}
      />
    </React.Fragment>
  );
};

const getContactActionsDefinition = ({
  urlFilters,
  viewer,
}: {
  urlFilters: SalesLeadsFiltersType;
  viewer?: UserModel;
}): ColumnDefinitionType<ProjectModel> => {
  return {
    headerLabel: 'Contact',
    cellComponent: (project) => {
      return viewer ? (
        <ContactActions project={project} urlFilters={urlFilters} viewer={viewer} />
      ) : undefined;
    },
  };
};

const ProjectLink = ({project, label}: {project: ProjectModel; label: string}) => {
  return (
    <LinkButton
      onPress={() => {
        window.open(`${Environment.getAppUrl()}/projects/${project.uuid}`, '_blank');
      }}
    >
      <Text.Link numberOfLines={1}>{label}</Text.Link>
    </LinkButton>
  );
};

const getIdentifierColumnDefinition = (): ColumnDefinitionType<ProjectModel> => {
  return {
    headerLabel: 'Project ID',
    cellComponent: (project) => {
      return <ProjectLink project={project} label={`Project ${project.identifier}`} />;
    },
    tooltip: (project) => (project.name ? Project.getDisplayText(project) : undefined),
  };
};

const getTableDefinition = ({
  responsive,
  viewer,
  refetch,
  urlFilters,
}: {
  responsive: ResponsiveType;
  viewer?: UserModel;
  refetch: () => void;
  urlFilters: SalesLeadsFiltersType;
}): ColumnDefinitionType<ProjectModel>[] => {
  const {isEnabledMovesListMultiBranchSupport} = viewer?.viewingOrganization.features || {};
  const isPrimaryOrganization = viewer?.viewingOrganization.isPrimary || false;
  const isOfficeAppEnabled = viewer?.viewingOrganization.isOfficeAppEnabled || false;
  const isProjectNameLink = responsive.desktop && isOfficeAppEnabled;

  return [
    {
      flex: 3,
      minWidth: 120,
      headerLabel: 'Project',
      cellText: isProjectNameLink ? undefined : (project) => Project.getDisplayText(project),
      cellComponent: !isProjectNameLink
        ? undefined
        : (project) => {
            return <ProjectLink project={project} label={Project.getDisplayText(project)} />;
          },
      tooltip: (project) => (project.name ? Project.getDisplayText(project) : undefined),
      secondary: responsive.desktop ? getProjectTagsDefinition() : undefined,
      mobileOptions: {
        isInHeader: true,
        rank: 0,
      },
    },
    ...List.insertIf(!responsive.desktop, {
      ...getProjectTagsDefinition(),
    }),
    {
      flex: 1.5,
      minWidth: 116,
      headerLabel: 'In Stage',
      ...(responsive.desktop
        ? {
            cellText: (project) =>
              project.salesStageUpdatedAt ? (
                <Badge label={Datetime.timeAgo(project.salesStageUpdatedAt, true)} />
              ) : undefined,
            tooltip: (project) =>
              project.salesStageUpdatedAt
                ? `In this stage since ${Datetime.convertToDisplayDate(
                    project.salesStageUpdatedAt,
                    Datetime.DISPLAY_SHORT_DATE,
                  )} at ${Datetime.toDisplayTime(Datetime.fromDatetime(project.salesStageUpdatedAt))}`
                : undefined,
          }
        : {
            cellText: (project) =>
              project.salesStageUpdatedAt
                ? `${Datetime.timeAgo(project.salesStageUpdatedAt, true)} in stage, since
                ${Datetime.convertToDisplayDate(
                  project.salesStageUpdatedAt,
                  Datetime.DISPLAY_SHORT_DATE,
                )}`
                : undefined,
          }),
      mobileOptions: {
        isInHeader: true,
        rank: 2,
      },
    },
    {
      flex: 3,
      minWidth: 120,
      headerLabel: 'Client',
      cellText: (project) => project.client.name,
      secondary:
        responsive.desktop && viewer
          ? getContactActionsDefinition({urlFilters, viewer})
          : undefined,
      mobileOptions: {
        isInHeader: true,
        rank: 1,
      },
    },
    ...List.insertIf(!responsive.desktop && !!viewer, {
      ...getContactActionsDefinition({urlFilters, viewer: viewer!}),
    }),
    {
      flex: 2,
      minWidth: 80,
      headerLabel: 'Last Touch',
      cellComponent: (project) => {
        const {lastCommunication} = project;
        if (lastCommunication) {
          return (
            <CommunicationStatusAction
              communication={lastCommunication}
              handleAction={() => {
                urlFilters.handleUpdate({
                  projectUuid: project.uuid,
                  widget: ProjectWidgetKind.CONVERSATIONS,
                });
              }}
            />
          );
        }
      },
      secondary: {
        cellText: (project) =>
          project.lastCommunication
            ? Datetime.abbreviatedTimeAgo(project.lastCommunication.communicationAt)
            : undefined,
      },
    },
    ...List.insertIf(!responsive.desktop && isOfficeAppEnabled, {
      ...getIdentifierColumnDefinition(),
    }),
    {
      flex: 3,
      minWidth: 120,
      headerLabel: 'Project Type',
      cellText: (project: ProjectModel) => project.projectType.name,
      cellIcon: () => Icon.Circle,
      cellIconColor: (project: ProjectModel) => project.projectType.color,
      cellIconSize: () => 8,
      secondary: {
        headerLabel: 'Labor Source',
        cellText: (project: ProjectModel) => Project.getDisplayLaborSource(project),
        isHidden: !isEnabledMovesListMultiBranchSupport || !isPrimaryOrganization,
      },
      mobileOptions: {
        isInHeader: true,
        rank: 3,
      },
    },
    {
      flex: 2,
      minWidth: 80,
      headerLabel: 'Project Dates',
      numberOfLines: 2,
      cellText: (project: ProjectModel) => {
        const {startDate, endDate} = project;
        if (!startDate || !endDate) {
          return null;
        }

        const displayStartDate = Datetime.convertToDisplayDate(
          startDate,
          Datetime.DISPLAY_SHORT_DATE,
        );
        const displayEndDate = Datetime.convertToDisplayDate(endDate, Datetime.DISPLAY_SHORT_DATE);

        if (startDate === endDate) {
          return displayStartDate;
        } else {
          return `${displayStartDate} - ${displayEndDate}`;
        }
      },
      ...(responsive.desktop ? {secondary: {headerLabel: '(Start - End)'}} : {}),
    },
    {
      flex: 3,
      minWidth: 120,
      headerLabel: 'Size',
      cellText: (project: ProjectModel) => project.size,
      secondary: {
        headerLabel: 'Survey',
        cellText: (project: ProjectModel) => project.inventory.surveyMethod?.name,
      },
    },
    {
      flex: 1.5,
      minWidth: 60,
      headerLabel: 'Price',
      cellText: (project) =>
        project.totalRevenue ? Currency.format({value: project.totalRevenue}) : undefined,
      secondary: {
        headerLabel: 'Weight',
        cellText: (project: ProjectModel) =>
          project.weight ? pluralize('lb', project.weight, true) : undefined,
      },
    },
    {
      flex: 2,
      minWidth: 80,
      headerLabel: 'Salesperson',
      cellText: (project) => project.bookedBy?.fullName,
      secondary: {
        headerLabel: 'Coordinator',
        cellText: (project: ProjectModel) => project.coordinatedBy?.fullName,
      },
    },
    {
      flex: 1.5,
      minWidth: 80,
      headerLabel: 'Referral',
      cellText: (project: ProjectModel) => project.referralSource,
    },
    {
      flex: 1.5,
      minWidth: 60,
      headerLabel: 'Comments',
      cellComponent: (project: ProjectModel) => {
        return (
          <ProjectCommentsButton
            project={project}
            handleViewComments={() => {
              urlFilters.handleUpdate({
                projectUuid: project.uuid,
                widget: ProjectWidgetKind.COMMENTS,
              });
            }}
          />
        );
      },
    },
  ];
};

const SalesLeadsTable = ({
  viewer,
  projects,
  loading,
  pageContentWidth,
  urlFilters,
  refetch,
}: {
  viewer?: UserModel;
  projects: ProjectModel[];
  loading: boolean;
  pageContentWidth: number;
  urlFilters: SalesLeadsFiltersType;
  refetch: () => void;
}) => {
  const responsive = useResponsive();
  const columns = getTableDefinition({
    responsive,
    viewer,
    refetch,
    urlFilters,
  });

  return (
    <React.Fragment>
      {!responsive.desktop && <Line />}
      <Table
        columnDefinitions={columns}
        isLoading={loading}
        items={projects}
        itemKey={'id'}
        emptyStateText={'No leads to display'}
        minWidth={Math.max(1168, pageContentWidth - GUTTER * 2)}
        isScrollable={responsive.desktop}
        horizontalScrollViewStyle={{paddingLeft: GUTTER, paddingRight: GUTTER}}
        onRowPress={(project) =>
          urlFilters.handleUpdate({
            projectUuid: project.uuid,
            widget: ProjectWidgetKind.PROJECT_DETAILS,
          })
        }
        activeRowIndex={_.findIndex(projects, {uuid: urlFilters.get('projectUuid')})}
      />
      {!responsive.desktop && <Line />}
    </React.Fragment>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
SalesLeadsTable.fragment = gql`
  ${ProjectTagsButton.fragment}
  ${ProjectCommentsButton.fragment}
  ${ProjectContactActions.fragment}
  ${Project.getDisplayText.fragment}
  ${Project.getDisplayLaborSource.fragment}
  ${CommunicationStatusAction.fragment}

  fragment SalesLeadsTable on Project {
    id
    uuid
    identifier
    createdAt
    startDate
    endDate
    size
    totalRevenue
    weight
    referralSource
    salesStageUpdatedAt
    lastCommunication {
      id
      communicationAt
      ...CommunicationStatusAction
    }
    client {
      id
      name
      primaryContact {
        id
        fullName
        phoneNumber
      }
    }
    projectType {
      id
      name
      color
    }
    bookedBy {
      id
      fullName
    }
    coordinatedBy {
      id
      fullName
    }
    inventory {
      id
      surveyMethod {
        id
        name
      }
    }
    ...ProjectTagsButton
    ...ProjectCommentsButton
    ...ProjectContactActions
    ...Project_getDisplayLaborSource
    ...Project_getDisplayText
  }
`;

SalesLeadsTable.viewerFragment = gql`
  ${OutgoingCallDrawer.fragment}
  ${Stage.getOrderedStages.fragment}

  fragment SalesLeadsTable_viewer on User {
    id
    viewingOrganization {
      id
      isPrimary
      isOfficeAppEnabled
      features {
        isEnabledMovesListMultiBranchSupport: isEnabled(feature: "MOVES_LIST_MULTI_BRANCH_SUPPORT")
      }
    }
    ...OutgoingCallDrawer
  }
`;

export default SalesLeadsTable;
